import { useEffect, useRef, useState } from 'react';
import useReducedMotion from './useReducedMotion';

const easeOut = (t) => 1 - Math.pow(1 - t, 3);

export function animateValue({ target, durationMs = 1600, reduced = false, onUpdate, onDone }) {
  if (reduced) {
    onUpdate(target);
    onDone && onDone();
    return () => {};
  }
  const start = performance.now();
  let raf;
  const tick = (now) => {
    const p = Math.min(1, (now - start) / durationMs);
    onUpdate(Math.round(target * easeOut(p)));
    if (p < 1) {
      raf = requestAnimationFrame(tick);
    } else {
      onDone && onDone();
    }
  };
  raf = requestAnimationFrame(tick);
  return () => cancelAnimationFrame(raf);
}

export default function useCountUp(target, { durationMs = 1600 } = {}) {
  const reduced = useReducedMotion();
  const [value, setValue] = useState(reduced ? target : 0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    if (reduced) { setValue(target); return; }
    started.current = false;
    const node = ref.current;
    if (!node) return;
    let cancel = () => {};
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        cancel = animateValue({ target, durationMs, reduced, onUpdate: setValue });
      }
    }, { threshold: 0.4 });
    io.observe(node);
    return () => { io.disconnect(); cancel(); };
  }, [target, durationMs, reduced]);

  return [value, ref];
}
